'use client';
import { Skeleton } from '@/components/ui/feedback/status/skeleton';

import {
  type ImgHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  CHECKERBOARD_BACKGROUND_STYLE,
  type TransparencyResult,
  UNKNOWN_TRANSPARENCY,
  detectKnownImageTransparency,
  readCachedTransparency,
  resolveImageTransparencyWithFallback,
} from '@/lib/ui/image-transparency';
import {
  DEFAULT_WSRV_WIDTHS,
  getWsrvImageUrl,
  isWsrvEnabled,
  normalizeWsrvWidths,
  resolveWsrvResponsiveImageSource,
} from '@/lib/ui/wsrv';

export type WsrvImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  widths?: number[];
  quality?: number;
  auto?: boolean;
  showCheckerboard?: boolean;
  proxy?: boolean;
  transparencyFallbackSrc?: string;
  onTransparencyChange?: (result: TransparencyResult) => void;
  onLoadingStateChange?: (state: ImageLoadingState) => void;
  loadingSkeleton?: boolean;
  animated?: boolean;
};

export type ImageLoadingState = 'loading' | 'loaded' | 'error';

export type ImageSourceState = {
  source: string;
  transparency: TransparencyResult;
};

type ImageSourceStatePatch = Partial<Pick<ImageSourceState, 'transparency'>>;

export function resolveImageSourceState(
  source: string,
  state: ImageSourceState,
  patch: ImageSourceStatePatch = {}
): ImageSourceState {
  const isSameSource = state.source === source;

  return {
    source,
    transparency: isSameSource
      ? (patch.transparency ?? state.transparency)
      : (patch.transparency ?? UNKNOWN_TRANSPARENCY),
  };
}

function areImageSourcesEqual(left: string, right: string) {
  if (left === right) {
    return true;
  }

  if (typeof document === 'undefined') {
    return false;
  }

  try {
    return new URL(left, document.baseURI).href === new URL(right, document.baseURI).href;
  } catch {
    return false;
  }
}

function isCurrentImageLoad(params: {
  image: HTMLImageElement;
  resolvedSrc: string;
  resolvedSrcSet?: string;
}) {
  const currentSrc = params.image.currentSrc || params.image.src;
  if (areImageSourcesEqual(currentSrc, params.resolvedSrc)) {
    return true;
  }

  return Boolean(
    params.resolvedSrcSet?.split(',').some((candidate) => {
      const source = candidate.trim().split(/\s+/)[0];
      return source ? areImageSourcesEqual(currentSrc, source) : false;
    })
  );
}

export function WsrvImage({
  src,
  widths,
  quality,
  proxy = true,
  sizes,
  srcSet,
  loading,
  auto,
  showCheckerboard = false,
  transparencyFallbackSrc,
  onTransparencyChange,
  onLoadingStateChange,
  onLoad,
  onError,
  loadingSkeleton = false,
  animated = false,
  style,
  alt,
  ...props
}: WsrvImageProps) {
  const normalizedSrc = src.trim();
  const wsrvEnabled = isWsrvEnabled(normalizedSrc, proxy);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const lastAutoWidthRef = useRef<number | null>(null);
  const transparencyRequestIdRef = useRef(0);
  const [autoWidth, setAutoWidth] = useState<number | null>(null);
  const autoEnabled = Boolean(auto);

  useEffect(() => {
    if (!autoEnabled) return;
    const img = imgRef.current;
    if (!img) return;
    let frameId: number | null = null;
    const updateAutoWidth = (cssWidth: number) => {
      if (!Number.isFinite(cssWidth) || cssWidth <= 0) return;
      const dpr =
        typeof window !== 'undefined'
          ? Math.max(1, window.devicePixelRatio || 1)
          : 1;
      const nextWidth = Math.ceil(cssWidth * dpr);
      if (lastAutoWidthRef.current === nextWidth) return;
      lastAutoWidthRef.current = nextWidth;
      setAutoWidth(nextWidth);
    };

    const queueUpdate = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(() => {
        frameId = null;
        updateAutoWidth(img.clientWidth);
      });
    };

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver((entries) => {
        const cssWidth = entries[0]?.contentRect.width ?? img.clientWidth;
        updateAutoWidth(cssWidth);
      });
      observer.observe(img);
      queueUpdate();
      return () => {
        observer.disconnect();
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
        }
      };
    }

    queueUpdate();
    window.addEventListener('resize', queueUpdate);
    return () => {
      window.removeEventListener('resize', queueUpdate);
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [autoEnabled]);

  const responsiveWidths =
    !autoEnabled && (widths?.length || sizes)
      ? (widths ?? DEFAULT_WSRV_WIDTHS)
      : [];
  const responsiveSource = autoEnabled
    ? null
    : resolveWsrvResponsiveImageSource({
        src: normalizedSrc,
        widths: responsiveWidths,
        quality,
        proxy,
        animated,
      });
  const resolvedSrc = wsrvEnabled
    ? autoEnabled
      ? getWsrvImageUrl(normalizedSrc, {
          width: autoWidth ?? undefined,
          quality,
          proxy,
          animated,
        })
      : responsiveSource?.src ?? normalizedSrc
    : responsiveSource?.src ?? normalizedSrc;
  const resolvedSrcSet = responsiveSource?.srcSet ?? srcSet;
  const resolvedSizes = resolvedSrcSet ? sizes : undefined;
  const loadKey = `${resolvedSrc}\n${resolvedSrcSet ?? ''}`;
  const shouldTrackLoading = loadingSkeleton || Boolean(onLoadingStateChange);
  const [settledImage, setSettledImage] = useState<{
    key: string;
    state: Exclude<ImageLoadingState, 'loading'>;
  } | null>(null);
  const loadingState = settledImage?.key === loadKey ? settledImage.state : 'loading';
  useEffect(() => {
    const image = imgRef.current;
    if (
      shouldTrackLoading &&
      image?.complete &&
      isCurrentImageLoad({ image, resolvedSrc, resolvedSrcSet })
    ) {
      setSettledImage({
        key: loadKey,
        state: image.naturalWidth > 0 ? 'loaded' : 'error',
      });
    }
  }, [loadKey, resolvedSrc, resolvedSrcSet, shouldTrackLoading]);
  useEffect(() => {
    onLoadingStateChange?.(loadingState);
  }, [loadingState, onLoadingStateChange]);
  const activeSourceRef = useRef(resolvedSrc);
  activeSourceRef.current = resolvedSrc;
  const [imageState, setImageState] = useState<ImageSourceState>(() => ({
    source: '',
    transparency: UNKNOWN_TRANSPARENCY,
  }));
  const renderState = resolveImageSourceState(resolvedSrc, imageState);
  const shouldShowCheckerboard =
    showCheckerboard && renderState.transparency.state === 'transparent';
  const shouldDetectTransparency = showCheckerboard || onTransparencyChange;
  const resolvedStyle = useMemo(
    () =>
      shouldShowCheckerboard
        ? { ...CHECKERBOARD_BACKGROUND_STYLE, ...style }
        : style,
    [shouldShowCheckerboard, style]
  );

  const commitTransparencyResult = useCallback(
    (source: string, result: TransparencyResult) => {
      if (source !== activeSourceRef.current) return;
      setImageState((current) =>
        resolveImageSourceState(source, current, {
          transparency: result,
        })
      );
      onTransparencyChange?.(result);
    },
    [onTransparencyChange]
  );

  const detectTransparency = useCallback(
    (img: HTMLImageElement) => {
      if (!shouldDetectTransparency) return;
      const currentSrc = img.currentSrc || img.src;
      if (!currentSrc) return;

      const cached = readCachedTransparency(
        currentSrc,
        transparencyFallbackSrc
      );
      if (cached.state !== 'unknown') {
        commitTransparencyResult(resolvedSrc, cached);
        return;
      }

      const immediate = detectKnownImageTransparency({
        image: img,
        currentSrc,
        fallbackSrc: transparencyFallbackSrc,
      });
      if (immediate.state !== 'unknown') {
        commitTransparencyResult(resolvedSrc, immediate);
        return;
      }

      const requestId = ++transparencyRequestIdRef.current;
      const detectFromUrl = async () => {
        const resolvedTransparency = await resolveImageTransparencyWithFallback(
          {
            currentSrc,
            fallbackSrc: transparencyFallbackSrc,
          }
        );
        if (transparencyRequestIdRef.current !== requestId) return;
        commitTransparencyResult(resolvedSrc, resolvedTransparency);
      };
      void detectFromUrl();
    },
    [
      commitTransparencyResult,
      resolvedSrc,
      shouldDetectTransparency,
      transparencyFallbackSrc,
    ]
  );

  useEffect(() => {
    transparencyRequestIdRef.current += 1;
    if (!shouldDetectTransparency || !resolvedSrc.trim()) {
      setImageState((current) =>
        resolveImageSourceState(resolvedSrc, current)
      );
      return;
    }
    const cached = readCachedTransparency(resolvedSrc, transparencyFallbackSrc);
    setImageState((current) =>
      resolveImageSourceState(resolvedSrc, current, {
        transparency: cached,
      })
    );
    onTransparencyChange?.(cached);
    // Cached/preloaded images can finish before the load handler or this effect.
    const image = imgRef.current;
    if (
      cached.state === 'unknown' &&
      image?.complete &&
      image.naturalWidth > 0 &&
      isCurrentImageLoad({ image, resolvedSrc, resolvedSrcSet })
    ) {
      detectTransparency(image);
    }
    return () => {
      transparencyRequestIdRef.current += 1;
    };
  }, [
    detectTransparency,
    onTransparencyChange,
    resolvedSrc,
    resolvedSrcSet,
    shouldDetectTransparency,
    transparencyFallbackSrc,
  ]);

  if (!resolvedSrc.trim()) {
    return null;
  }

  const image = (
    // biome-ignore lint/a11y/useAltText: WsrvImage forwards alt through typed image props.
    <img
      ref={imgRef}
      src={resolvedSrc}
      srcSet={resolvedSrcSet}
      sizes={resolvedSizes}
      loading={loading ?? 'lazy'}
      style={loadingSkeleton && loadingState === 'loading' ? style : resolvedStyle}
      alt={alt}
      data-transparency={renderState.transparency.state}
      data-image-loading={loadingSkeleton ? loadingState : undefined}
      aria-busy={loadingSkeleton ? loadingState === 'loading' : undefined}
      onLoad={(event) => {
        if (
          !isCurrentImageLoad({
            image: event.currentTarget,
            resolvedSrc,
            resolvedSrcSet,
          })
        ) {
          return;
        }
        if (shouldDetectTransparency) {
          detectTransparency(event.currentTarget);
        }
        if (shouldTrackLoading) {
          setSettledImage({ key: loadKey, state: 'loaded' });
        }
        onLoad?.(event);
      }}
      onError={(event) => {
        if (!isCurrentImageLoad({ image: event.currentTarget, resolvedSrc, resolvedSrcSet })) return;
        if (shouldTrackLoading) {
          setSettledImage({ key: loadKey, state: 'error' });
        }
        onError?.(event);
      }}
      {...props}
    />
  );
  return loadingSkeleton ? <Skeleton asChild active={loadingState === 'loading'}>{image}</Skeleton> : image;
}
